import React, { ReactNode, createContext, useCallback, useContext, useState } from 'react';
import {
  AddLevelBody,
  LevelAttributes,
  UpdateLevelBody,
} from '@loyalty/shared-common/gamification-api-client';
import { Alert } from '@mui/material';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { uuid } from '@loyalty/shared-common';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { Loader } from '../shared/layout/Loader';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useProgram } from '../loyalty/ProgramContext';
import { useSelectiveEffect } from '../shared/use-selective-effect';

export interface LevelsContextInterface {
  levels: LevelAttributes[];
  selectedLevel: LevelAttributes | undefined;
  createLevel: () => void;
  addLevel: (level: AddLevelBody) => void;
  updateLevel: (level: UpdateLevelBody) => void;
  deleteLevel: (levelId: string) => Promise<void>;
  deleteUnsavedLevel: (levelId: string) => void;
  deleteUnsavedLevels: () => void;
  setSelectedLevel: (level: LevelAttributes) => void;
  isLoading: IsLoading;
}

export const LevelsContext = createContext<LevelsContextInterface>({} as any);

export function useLevels() {
  return useContext(LevelsContext);
}

export function LevelsProvider({ children }: { children: ReactNode }) {
  const { program } = useProgram();
  const { gamificationApi, managerApi } = useApi();
  const [levels, setLevels] = useState<LevelAttributes[]>([]);
  const [error, setError] = useState<any>();
  const [selectedLevel, setSelectedLevel] = useState<LevelAttributes | undefined>();
  const { notify } = useAlerts();
  const { t } = useTranslation();

  const { addLoader, removeLoader, isLoading } = useLoaders({ getLevels: true });

  const createLevel = useCallback(() => {
    const level = {
      id: uuid(),
      i18nAttributes: program.languages.reduce(
        (langs, current) => ({
          ...langs,
          [current]: {
            name: '',
            description: '',
            image: '',
          },
        }),
        {},
      ),
      createdAt: undefined,
      updatedAt: undefined,
      deletedAt: undefined,
      minCurrencyAmount: undefined,
      isNew: true,
    } as any as Omit<LevelAttributes, 'programId'> as LevelAttributes;

    setLevels(prev => [level, ...prev]);
    setSelectedLevel(level);
  }, [program.languages]);

  const addLevel = useCallback(
    (level: AddLevelBody) => {
      addLoader('addLevel');
      gamificationApi
        .addLevel(program.id, level)
        .then(newLevel => {
          setLevels(prev => prev.map(lvl => (lvl.id === level.id ? newLevel : lvl)));
          setSelectedLevel(newLevel);
          notify(t('shared.saved'), 'success');
        })
        .catch(error => {
          setError(error);
          notify(t('levels.errors.save-error', { error }), 'error');
        })
        .finally(() => removeLoader('addLevel'));
    },
    [addLoader, gamificationApi, notify, program.id, removeLoader, t],
  );

  const updateLevel = useCallback(
    (level: UpdateLevelBody) => {
      addLoader('updateLevel');
      gamificationApi
        .updateLevel(level)
        .then(updatedLevel => {
          setLevels(prev => prev.map(lvl => (lvl.id === level.id ? updatedLevel : lvl)));
          setSelectedLevel(updatedLevel);
          notify(t('shared.saved'), 'success');
        })
        .catch(error => {
          setError(error);
          notify(t('levels.errors.save-error', { error }), 'error');
        })
        .finally(() => removeLoader('updateLevel'));
    },
    [addLoader, gamificationApi, notify, removeLoader, t],
  );

  const deleteLevel = useCallback(
    async (levelId: string) => {
      addLoader('deleteLevel');

      const level = levels.find(({ id }) => id === levelId);

      if (level?.['isNew']) {
        setLevels(prev => {
          const newLevels = prev.filter(level => level.id !== levelId);
          setSelectedLevel(undefined);
          return newLevels;
        });
        removeLoader('deleteLevel');
      } else {
        await managerApi
          .deleteLevel(program.id, levelId)
          .then(() => {
            setLevels(prev => {
              const newLevels = prev.filter(level => level.id !== levelId);
              setSelectedLevel(undefined);
              return newLevels;
            });
          })
          .catch(error => {
            setError(error);
            notify(t('levels.errors.delete-level', { error }), 'error');
          })
          .finally(() => removeLoader('deleteLevel'));
      }
    },
    [addLoader, levels, managerApi, notify, program.id, removeLoader, t],
  );

  const deleteUnsavedLevel = useCallback((levelId: string) => {
    setSelectedLevel(undefined);
    setLevels(levels => levels.filter(level => level.id !== levelId));
  }, []);

  const deleteUnsavedLevels = useCallback(() => {
    setSelectedLevel(undefined);
    setLevels(levels => levels.filter(level => !(level as any).isNew));
  }, []);

  const getLevels = useCallback(() => {
    addLoader('getLevels');

    gamificationApi
      .getLevels(program.id)
      .then(levels => setLevels(levels))
      .catch(setError)
      .finally(() => removeLoader('getLevels'));
  }, [addLoader, gamificationApi, program.id, removeLoader]);

  useSelectiveEffect(
    () => {
      getLevels();
    },
    [getLevels, program.id],
    [program.id],
  );

  const context = {
    levels,
    selectedLevel,
    createLevel,
    addLevel,
    updateLevel,
    deleteLevel,
    deleteUnsavedLevel,
    deleteUnsavedLevels,
    setSelectedLevel,
    isLoading,
  };

  return isLoading('getLevels') ? (
    <Loader />
  ) : error ? (
    <Alert severity="error">{error.toString()}</Alert>
  ) : (
    <LevelsContext.Provider value={context}>{children}</LevelsContext.Provider>
  );
}
